import {CartItemDisplay} from "@eshop/types/categories.types.ts";
import {parsePrice} from "@tools/utils/common.tsx";
import { IMG_NOT_FOUND } from '@tools/utils/imageUtils.tsx';
import {ShoppingCartOutlined} from "@ant-design/icons";
import React from "react";
import {CartItem} from "@eshop/types/cart.types.ts";
import {ChartItemDescription, ChartItemDescriptionView} from "@eshop/pages/cart/Bloques.tsx";
import {AppDispatch} from "@store/store.tsx";
import {ErrorPayload, ErrorResponseState} from "@tools/types/common.types.ts";
import {Button, notification} from "antd";
import { UpdateQtyCartLineAsync, extractStockErrorDetail, notifyStockBlocked } from '@store/slices/cartSlice.tsx';
import { logError } from '@tools/LogsHandler.tsx';
import { ItemImage } from '@eshop/pages/catalogue/CatalogueUtils.tsx';


export const getCartDrawerItems = (
    cartItems: CartItem[],
    currency: string | undefined,
    setCurrency: React.Dispatch<React.SetStateAction<string | undefined>>,
    openPopConfirmItemDelete: number | null,
    setOpenPopConfirmItemDelete: React.Dispatch<React.SetStateAction<number | null>>,
    frozzenCart: boolean = false,
): CartItemDisplay[] => {

    if (cartItems.length > 0) {
        return cartItems.map((line: CartItem) => {
          // console.log('line', line);
            const image = (line.item?.images && line.item?.images.length > 0 ? line.item.images[0] : null) || line.item?.parent?.image;
            const url_image = image?.thumbnails?.urls.small || IMG_NOT_FOUND;

            const parsedPrice = parsePrice(line.item?.price);
            if (!currency && parsedPrice?.currency) setCurrency(parsedPrice.currency);

            const columnsOptions = [
                {
                    key: 'name',
                    title: 'Tipo',
                    dataIndex: 'name',
                    className: "px-0",
                },
                {
                    key: 'value',
                    title: 'Valor',
                    dataIndex: 'verbose',
                    className: "pe-0 text-right",
                },
            ]

            return {
                key: line.id.toString(),
                title: line.item?.fullName || line.item?.full_name || line.name,
                quantity: line.quantity,
                price: line.subtotal.totalSinDesc.toFixed(2),
                currency: parsedPrice?.currency || 'ARS',
                imageUrl: url_image,
                description: (
                    frozzenCart?
                        <ChartItemDescriptionView
                            item={line}
                            columnsOptions={columnsOptions}
                            currency={currency}
                        /> :
                        <ChartItemDescription
                            item={line}
                            columnsOptions={columnsOptions}
                            setOpenPopConfirmItemDelete={setOpenPopConfirmItemDelete}
                            openPopConfirmItemDelete={openPopConfirmItemDelete}
                            currency={currency}
                        />
                ),
                imageElement: (
                    <ItemImage
                      image={image}
                      preview={false}
                      width={100}
                      avatar={true}
                    />
                )
            };
        });
    } else {
        return [{
            key: '0',
            title: "No hay productos en el carrito",
            quantity: 0,
            price: "0.00",
            currency: 'ARS',
            imageUrl: undefined,
            description: (
                <div className="d-flex justify-content-center">
                    <ShoppingCartOutlined style={{fontSize: 50, marginTop: 10}}/>
                </div>
            ),
            imageElement: (<span></span>)
        }];
    }
};


/**
 * Actualiza la cantidad de un producto en el carrito.
 * @param item - El item del carrito.
 * @param newQuantity - La nueva cantidad que se desea establecer.
 * @param dispatch - Función dispatch de Redux.
 * @param setQuantity - Función para establecer la cantidad en el estado local.
 * @param setErrorMsg - Función para establecer el mensaje de error si ocurre uno.
 */
export const updateCartItemQuantity = async (
    item: CartItem,
    newQuantity: number,
    dispatch: AppDispatch,
    setQuantity: (qty: number) => void,
    setErrorMsg: (errorMsg: string | undefined) => void,
) => {
    if (item.quantity !== newQuantity) {
        const params = { prodId: item.item_id, quantity: newQuantity };

        try {
            const action = await dispatch(UpdateQtyCartLineAsync(params));

            let updatedQuantity = item.quantity; // Default to current quantity

            if (UpdateQtyCartLineAsync.fulfilled.match(action)) {
                updatedQuantity = action.payload.data.quantity;
            } else if (UpdateQtyCartLineAsync.rejected.match(action)) {
                const stockError = extractStockErrorDetail(action.payload);
                if (stockError) {
                    notifyStockBlocked(stockError);
                    setErrorMsg(stockError.message ?? 'No hay stock disponible.');
                } else {
                    const errorData = action.payload ? (action.payload as ErrorResponseState) : undefined;
                    const errors = errorData?.response?.data;

                    if (errors && typeof errors === 'object' && 'quantity' in errors) {
                        const quantityError = (errors as ErrorPayload)['quantity'];

                        if (Array.isArray(quantityError)) {
                            setErrorMsg(quantityError.join(', '));
                        } else if (typeof quantityError === 'string') {
                            setErrorMsg(quantityError);
                        }
                    }
                }
            }

            // Update the quantity if the server response is different
            if (updatedQuantity !== newQuantity) {
                setQuantity(updatedQuantity);
            }
        } catch (error) {
          logError("Error al actualizar la cantidad. Err: ", error)
          setErrorMsg("Error al actualizar la cantidad."); // Set a general error message if something goes wrong
        }
    }
};


export const notificationAddItem = (checkoutURL: string)=> {
    // Muestra la notificación al agregar un ítem con éxito
    notification.success({
        message: '¡Producto agregado al carrito!',
        description: 'El producto ha sido agregado con éxito. ¿Te gustaría finalizar la compra?',
        actions: (
            <Button
                key="Pagar"
                block
                href={checkoutURL}>
                Ir a pagar
            </Button>
        ),
        duration: 5, // Duración de la notificación (en segundos)
    });
}
